import { useDebounceValue } from 'noddi-util';
import { useEffect, useState } from 'react';

/** Shared debounce + external-sync scaffolding for single-value text/number filters. */
export const useDebouncedFilterInput = <T extends string | number>(
  selectedValue: T | null | undefined,
  onChange: (value: T | null) => void,
  parse: (raw: string) => T | null,
  serialize: (value: T | null | undefined) => string
) => {
  const [localValue, setLocalValue] = useState<string>(serialize(selectedValue));
  const debouncedValue = useDebounceValue({ value: localValue, delay: 500 });

  useEffect(() => {
    const nextValue = parse(debouncedValue);
    const currentValue = selectedValue ?? null;

    if (nextValue !== currentValue) {
      onChange(nextValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires on debounced input only; adding selectedValue/onChange/parse would re-emit the stale input after an external clear
  }, [debouncedValue]);

  // Sync local state when selectedValue changes externally (e.g., when clearing).
  // Adjust during render rather than in an effect to avoid a cascading re-render.
  const [syncedValue, setSyncedValue] = useState(selectedValue);
  if (syncedValue !== selectedValue) {
    setSyncedValue(selectedValue);
    setLocalValue(serialize(selectedValue));
  }

  return { localValue, setLocalValue };
};
